import { ActionTree } from 'vuex';
import { RootState } from '../types';
import { CommunitySessionsState, CommunitySessionsActions, CommunitySessionsMutations } from './types';
import { api } from '../api';
import { getIdOrCurrent } from '@/utils/api';
import i18n from '@/plugins/i18n';
import { ShowErrorNotification } from '../notifications';

export const actions: ActionTree<CommunitySessionsState, RootState> = {
  async [CommunitySessionsActions.LoadUpcomingCommunintySessionPage]({ dispatch }, { tenantRef, page }) {
    try {
      const response = await api.get(`tenants/${getIdOrCurrent(tenantRef)}/community-sessions/upcoming`, {
        params: { page: page },
      });
      return response.data;
    } catch (e) {
      await dispatch(new ShowErrorNotification(i18n.t('notifications.generalError').toString()), {
        root: true,
      });
      return false;
    }
  },

  async [CommunitySessionsActions.LoadCommunintySession]({ dispatch }, { tenantRef, sessionId }) {
    try {
      const response = await api.get(`tenants/${getIdOrCurrent(tenantRef)}/community-sessions/${sessionId}`);
      return response.data;
    } catch (e) {
      await dispatch(new ShowErrorNotification(i18n.t('notifications.generalError').toString()), {
        root: true,
      });
      return false;
    }
  },

  async [CommunitySessionsActions.SaveCommunitySession]({ commit, dispatch, rootState }, { dto, tenantRef }) {
    try {
      const response = await api.postForm(`tenants/${getIdOrCurrent(tenantRef)}/community-sessions`, dto);
      if (response?.data?.id) {
        commit(CommunitySessionsMutations.SetNewCommunitySessionId, response.data.id);

        // After creating a new session, refresh the user's sessions list
        const me = rootState.users.me;
        const tenant = rootState.tenant.tenant;

        // Dispatch the action to reload the sessions list
        await dispatch(CommunitySessionsActions.LoadCommunitySessionsForUser, {
          tenantRef: tenant,
          userRef: me,
        });
      }
    } catch (e) {
      await dispatch(new ShowErrorNotification(i18n.t('notifications.generalError').toString()), {
        root: true,
      });
      return false;
    }
  },

  async [CommunitySessionsActions.EditCommunitySession]({ dispatch, rootState }, { id, dto, tenantRef }) {
    try {
      const response = await api.patchForm(`tenants/${getIdOrCurrent(tenantRef)}/community-sessions/${id}`, dto);

      // After editing, refresh the user's sessions list
      if (response?.data?.id) {
        // Get current user from rootState instead of dispatching users/GetMe
        const me = rootState.users.me;
        const tenant = rootState.tenant.tenant;

        // Dispatch the action to reload the sessions list
        await dispatch(CommunitySessionsActions.LoadCommunitySessionsForUser, {
          tenantRef: tenant,
          userRef: me,
        });

        return response.data;
      }
    } catch (e) {
      console.log(e);
      await dispatch(new ShowErrorNotification(i18n.t('notifications.generalError').toString()), {
        root: true,
      });
      return false;
    }
  },

  async [CommunitySessionsActions.LoadCommunitySessionsForUser]({ commit, dispatch }, { tenantRef, userRef }) {
    try {
      const response = await api.get(`tenants/${getIdOrCurrent(tenantRef)}/community-sessions/user/${userRef.id}`);
      if (response?.data) {
        commit(CommunitySessionsMutations.SetCommunitySessionForUser, response.data);
      }
    } catch (e) {
      await dispatch(new ShowErrorNotification(i18n.t('notifications.generalError').toString()), {
        root: true,
      });
      return false;
    }
  },

  async [CommunitySessionsActions.SubscribeToCommunitySession]({ dispatch }, { tenantRef, sessionId }) {
    try {
      const response = await api.post(`tenants/${getIdOrCurrent(tenantRef)}/community-sessions/subscribe/${sessionId}`);
      return response.data;
    } catch (e) {
      await dispatch(new ShowErrorNotification(i18n.t('notifications.generalError').toString()), {
        root: true,
      });
      return false;
    }
  },

  async [CommunitySessionsActions.UnsubscribeToCommunitySession]({ dispatch }, { tenantRef, sessionId }) {
    try {
      const response = await api.post(
        `tenants/${getIdOrCurrent(tenantRef)}/community-sessions/unsubscribe/${sessionId}`,
      );
      return response.data;
    } catch (e) {
      await dispatch(new ShowErrorNotification(i18n.t('notifications.generalError').toString()), {
        root: true,
      });
      return false;
    }
  },

  [CommunitySessionsActions.ChangeCreateCommunitySessionVisible]({ commit }, { visible }) {
    commit(CommunitySessionsMutations.SetCreateCommunitySessionVisible, visible);
  },

  [CommunitySessionsActions.CommunitySessionCreatedSuccessfully]({ commit }, { id }) {
    commit(CommunitySessionsMutations.SetNewCommunitySessionId, id);
  },

  [CommunitySessionsActions.CloseCommunitySessionCreatedSuccessfull]({ commit }) {
    commit(CommunitySessionsMutations.SetNewCommunitySessionId, undefined);
    commit(CommunitySessionsMutations.SetCreateCommunitySessionVisible, false);
  },

  async [CommunitySessionsActions.DeleteCommunitySession]({ dispatch, rootState }, { sessionId, tenantRef }) {
    try {
      console.log('Deleting session:', sessionId, 'from tenant:', getIdOrCurrent(tenantRef));
      const response = await api.delete(`tenants/${getIdOrCurrent(tenantRef)}/community-sessions/${sessionId}`);
      console.log('Delete session response:', response);

      // After deleting, refresh the user's sessions list using the current user from the store
      const me = rootState.users.me;
      const tenant = rootState.tenant.tenant;
      console.log('Current user for refresh:', me);
      console.log('Current tenant for refresh:', tenant);

      // Dispatch the action directly with the correct parameters
      await dispatch(CommunitySessionsActions.LoadCommunitySessionsForUser, {
        tenantRef: tenant,
        userRef: me,
      });

      return true;
    } catch (e) {
      console.error('Error deleting session:', e);
      await dispatch(new ShowErrorNotification(i18n.t('notifications.generalError').toString()), {
        root: true,
      });
      return false;
    }
  },
};
